import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { galleryCards } from "@/mocks/gallery";

interface HorizontalGalleryProps {
  /** 0 → 1 : 0 = cards fully off-screen right, 1 = last card fully in from left */
  progress: number;
  /** 0 → 1 : entrance opacity (fades cards in during first bit of the phase) */
  entrance: number;
}

export default function HorizontalGallery({
  progress,
  entrance,
}: HorizontalGalleryProps) {
  const target = Math.max(0, Math.min(1, progress));

  // ---- Elastic smoothing ----
  // We lerp a rendered value toward the raw scroll-driven target every frame,
  // so the strip feels like it's tethered to scroll with a soft spring instead
  // of being locked pixel-perfectly to it.
  const [smoothed, setSmoothed] = useState(target);
  const smoothedRef = useRef(target);
  const targetRef = useRef(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    targetRef.current = target;
    if (rafRef.current !== null) return;

    const tick = () => {
      // Low lerp factor = slower, more elastic follow.
      const next =
        smoothedRef.current + (targetRef.current - smoothedRef.current) * 0.08;
      smoothedRef.current = next;
      setSmoothed(next);

      if (Math.abs(targetRef.current - next) > 0.0005) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        smoothedRef.current = targetRef.current;
        setSmoothed(targetRef.current);
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [target]);

  const clamped = smoothed;

  // Fatter cards: each card now occupies ~34vw of the strip (was ~26vw).
  // Including gaps, budget ~36vw of horizontal travel per card.
  const cardBudgetVW = 36;

  // The strip should slide from a small offset on the right to a negative
  // offset that reveals all cards + some breathing room on the left.
  const startVW = 15; // start with the strip just off the right edge
  const endVW = -(galleryCards.length * cardBudgetVW - 60); // reveal the whole strip
  const currentVW = startVW + (endVW - startVW) * clamped;

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Sticky header line */}
      <div
        className="absolute top-8 md:top-12 left-8 md:left-14 z-20 flex items-center gap-3 pointer-events-none"
        style={{ opacity: entrance }}
      >
        <span className="w-8 h-px bg-white/60" />
        <span className="text-[10px] uppercase tracking-[0.4em] text-white/70 whitespace-nowrap">
          Chapter Two · Selected Rooms
        </span>
      </div>

      {/* Horizontal strip */}
      <div
        className="absolute top-1/2 -translate-y-1/2 flex items-center gap-6 md:gap-8 pl-8 md:pl-14 pr-8 md:pr-14"
        style={{
          transform: `translate(calc(${currentVW}vw), -50%)`,
          opacity: entrance,
          willChange: "transform, opacity",
        }}
      >
        {galleryCards.map((card, idx) => {
          // Slight per-card parallax: earlier cards drift a bit further left
          // to feel like they're layered, not glued together.
          const parallax = (idx - (galleryCards.length - 1) / 2) * 10 * clamped;
          return (
            <Link
              to={`/projects/${card.slug}`}
              key={card.id}
              className="relative shrink-0 group cursor-pointer no-underline"
              style={{ transform: `translateX(${parallax}px)` }}
            >
              <article className="relative">
                <div
                  className="relative overflow-hidden rounded-lg bg-neutral-900"
                  style={{
                    width: "clamp(340px, 34vw, 560px)",
                    height: "clamp(420px, 66vh, 720px)",
                  }}
                >
                  <img
                    src={card.image}
                    alt={`${card.title} — ${card.subtitle}`}
                    className="absolute inset-0 w-full h-full object-cover object-top grayscale brightness-[0.55] transition-[transform,filter] duration-[900ms] ease-out group-hover:scale-[1.06] group-hover:grayscale-0 group-hover:brightness-100"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/20 transition-opacity duration-[900ms] ease-out group-hover:opacity-50" />

                  {/* Hover cue — quiet arrow that fades in on hover */}
                  <div className="absolute top-5 right-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <span className="w-7 h-7 rounded-full border border-white/70 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-white text-sm" />
                    </span>
                  </div>

                  <div className="absolute top-5 left-5 flex items-center gap-2">
                    <span className="text-[9px] uppercase tracking-[0.35em] text-white/80">
                      {card.label}
                    </span>
                  </div>

                  <div className="absolute bottom-5 left-5 right-5">
                    <div className="text-[9px] uppercase tracking-[0.35em] text-white/60 mb-2">
                      {card.subtitle}
                    </div>
                    <h3
                      className="text-white font-black uppercase leading-none tracking-tight"
                      style={{ fontSize: "clamp(24px, 2.4vw, 38px)" }}
                    >
                      {card.title}
                    </h3>
                    {/* Slim underline that stretches on hover */}
                    <div className="mt-3 h-px w-8 bg-white/60 transition-all duration-500 group-hover:w-24 group-hover:bg-white" />
                  </div>
                </div>
              </article>
            </Link>
          );
        })}
      </div>
    </div>
  );
}